import DesignTokens from '@tableau/design-tokens';
import {
  ButtonType,
  ButtonWidget,
  CompactInlineAlertWidget,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  InlineAlertType,
  TextFieldWidget
} from '@tableau/widgets';
import * as clipboard from 'clipboard-polyfill';
import React from 'react';

import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { Filter } from '~src/commonInfra/utils/Filter';
import { Tableau } from '~src/commonInfra/utils/TableauLog';
import { IProjectInfo } from '~src/projects/models/IProjectInfo';
import { Projects } from '~src/projects/resources/Projects';
import { useSelector } from '~src/settings/general-settings/utils';
import { NamedItemKeys } from '~src/spacesPlatform/models/resourceParts';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

import { IAuthFederationConnector } from './service/AuthFederationService';
import { connectorClassLabel, WIF_CONNECTOR_CLASSES } from './WifConnectorClasses';

const STYLE_PREFIX = 'jwt-preview-modal';
const styles = {
  dialogContent: cssClass(STYLE_PREFIX, 'content', {
    minWidth: '560px',
    maxWidth: '760px'
  }),
  projectInputBlock: cssClass(STYLE_PREFIX, 'project-input-block', {
    marginBottom: '16px',
    position: 'relative'
  }),
  projectInputHelperText: cssClass(STYLE_PREFIX, 'project-input-helper', {
    marginTop: '4px',
    fontSize: CommonStyles.Sizes.NormalFontSize,
    color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.NormalTextColor)
  }),
  projectResultsList: cssClass(STYLE_PREFIX, 'project-results-list', {
    listStyle: 'none',
    margin: '4px 0 0 0',
    padding: 0,
    border: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.default, CommonStyles.Colors.BorderColor)}`,
    borderRadius: '4px',
    maxHeight: '180px',
    overflowY: 'auto'
  }),
  projectResultButton: cssClass(STYLE_PREFIX, 'project-result-button', {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '6px 8px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: CommonStyles.Sizes.NormalFontSize
  }),
  // The connector block holds either the dropdown (label + <select>) or the
  // no-connectors warning. The 16px gap below belongs to the dropdown — the
  // warning brings its own spacing — so the margin is on connectorSelect.
  connectorBlock: cssClass(STYLE_PREFIX, 'connector-block'),
  fieldLabel: cssClass(STYLE_PREFIX, 'field-label', {
    display: 'block',
    marginBottom: '4px',
    fontWeight: 'bold'
  }),
  connectorSelect: cssClass(STYLE_PREFIX, 'connector-select', {
    width: '100%',
    padding: '6px',
    marginBottom: '16px',
    fontSize: CommonStyles.Sizes.NormalFontSize
  }),
  luidsCard: cssClass(STYLE_PREFIX, 'luids-card', {
    marginBottom: '16px',
    padding: '12px',
    border: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.default, CommonStyles.Colors.BorderColor)}`,
    borderRadius: '4px'
  }),
  luidRow: cssClass(STYLE_PREFIX, 'luid-row', {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px'
  }),
  luidRowSpaced: cssClass(STYLE_PREFIX, 'luid-row-spaced', {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '8px',
    gap: '8px'
  }),
  luidLabel: cssClass(STYLE_PREFIX, 'luid-label', {
    fontWeight: 'bold',
    flexShrink: 0
  }),
  luidValue: cssClass(STYLE_PREFIX, 'luid-value', {
    flex: 1,
    minWidth: 0,
    marginRight: '8px',
    fontFamily: 'monospace',
    wordBreak: 'break-all'
  }),
  copyButton: cssClass(STYLE_PREFIX, 'copy-button', {
    fontSize: CommonStyles.Sizes.NormalFontSize,
    padding: '4px 8px'
  }),
  claimsBlock: cssClass(STYLE_PREFIX, 'claims-block', {
    marginBottom: '8px'
  }),
  claimsHeader: cssClass(STYLE_PREFIX, 'claims-header', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px'
  }),
  claimsPre: cssClass(STYLE_PREFIX, 'claims-pre', {
    padding: '12px',
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, CommonStyles.Colors.Grey245),
    border: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.default, CommonStyles.Colors.BorderColor)}`,
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: CommonStyles.Sizes.NormalFontSize,
    overflowX: 'auto'
  })
};

export const JWT_PREVIEW_MODAL_TEST_ID = 'jwt-preview-modal';
export const JWT_PREVIEW_PROJECT_INPUT_TEST_ID = 'jwt-preview-project-input';
export const JWT_PREVIEW_PROJECT_RESULT_TEST_ID = 'jwt-preview-project-result';
export const JWT_PREVIEW_CONNECTOR_SELECT_TEST_ID = 'jwt-preview-connector-select';
export const JWT_PREVIEW_PROJECT_LUID_TEST_ID = 'jwt-preview-project-luid';
export const JWT_PREVIEW_SITE_LUID_TEST_ID = 'jwt-preview-site-luid';
export const JWT_PREVIEW_CLAIMS_TEST_ID = 'jwt-preview-claims';
export const JWT_PREVIEW_COPY_PROJECT_LUID_TEST_ID = 'jwt-preview-copy-project-luid';
export const JWT_PREVIEW_COPY_SITE_LUID_TEST_ID = 'jwt-preview-copy-site-luid';
export const JWT_PREVIEW_COPY_CLAIMS_TEST_ID = 'jwt-preview-copy-claims';
export const JWT_PREVIEW_SELECT_PROMPT_TEST_ID = 'jwt-preview-select-prompt';
export const JWT_PREVIEW_NO_CONNECTORS_TEST_ID = 'jwt-preview-no-connectors';
export const JWT_PREVIEW_COPY_ERROR_TEST_ID = 'jwt-preview-copy-error';
export const JWT_PREVIEW_LIFETIME_FALLBACK_TEST_ID = 'jwt-preview-lifetime-fallback';
export const JWT_PREVIEW_CONNECTOR_REMOVED_TEST_ID = 'jwt-preview-connector-removed';
export const JWT_PREVIEW_CLOSE_TEST_ID = 'jwt-preview-close';

export function jwtPreviewProjectResultTestId(luid: string): string {
  return `${JWT_PREVIEW_PROJECT_RESULT_TEST_ID}-${luid}`;
}

const PROJECT_SEARCH_LIMIT = 50;

// Fallback lifetime applied when the upstream config delivers a missing or
// non-numeric tokenLifetimeSeconds. Without it, exp would be NaN and
// JSON.stringify would emit `null` — confusing for an admin pasting the claim
// into an IAM trust policy. One hour matches the JWT default elsewhere.
const DEFAULT_TOKEN_LIFETIME_SECONDS = 3600;

interface IJwtPreviewClaims {
  sub: string;
  site: string;
  aud: string;
  exp: number;
  iat: number;
  jti: string;
  iss: string;
}

interface JwtPreviewModalProps {
  onClose: () => void;
  issuerUrl: string;
  tokenLifetimeSeconds: number;
  // Pre-loaded by the parent (WifConnectorsSection) — the modal does not call
  // listConnectors itself, so the audience shown matches what the parent has
  // in state without a duplicate fetch.
  connectors: IAuthFederationConnector[];
}

interface IBuildClaimsInput {
  project: IProjectInfo | null;
  selected: IAuthFederationConnector | null;
  siteLuid: string;
  issuerUrl: string;
  tokenLifetimeSeconds: number;
  iat: number | null;
}

interface IBuildClaimsResult {
  claims: IJwtPreviewClaims;
  // True when buildClaims fell back to DEFAULT_TOKEN_LIFETIME_SECONDS. The
  // component renders a visible warning so the admin sees the synthesised
  // lifetime rather than copying a wrong-but-plausible exp.
  lifetimeFallbackApplied: boolean;
}

// Stable key order for the rendered claim JSON. The visual order is part of
// the surface admins paste into IAM trust policies — pin it explicitly so a
// future refactor that builds the claim via spread cannot drift it.
const CLAIM_KEY_ORDER: ReadonlyArray<keyof IJwtPreviewClaims> = [
  'sub',
  'site',
  'aud',
  'exp',
  'iat',
  'jti',
  'iss'
];

function buildClaims(input: IBuildClaimsInput): IBuildClaimsResult | null {
  const { project, selected, siteLuid, issuerUrl, tokenLifetimeSeconds, iat } = input;
  // siteLuid is '' when the site slice is missing (see SessionInfoSelectors).
  // A blank site LUID makes the preview useless — short-circuit to null so the
  // caller can render the select-prompt state instead.
  if (!project || !selected || !siteLuid || iat === null) {
    return null;
  }
  // Coerce to a number: the admin API delivers tokenLifetimeSeconds as a
  // string, so a bare `iat + tokenLifetimeSeconds` would concatenate. If the
  // upstream config is partial/non-numeric, Number() yields NaN, exp becomes
  // NaN, and JSON.stringify emits `null` — a JWT-shaped object with a
  // confusing null exp. Fall back to DEFAULT_TOKEN_LIFETIME_SECONDS and let
  // the caller surface the fallback in the UI; silent fallback would defeat
  // the widget's purpose.
  const coerced = Number(tokenLifetimeSeconds);
  let lifetime: number;
  let lifetimeFallbackApplied = false;
  if (isFinite(coerced)) {
    lifetime = coerced;
  } else {
    Tableau.Logger().warn(
      'JwtPreviewModal: tokenLifetimeSeconds is not a finite number; using default lifetime.'
    );
    lifetime = DEFAULT_TOKEN_LIFETIME_SECONDS;
    lifetimeFallbackApplied = true;
  }
  return {
    claims: {
      sub: project.luid,
      site: siteLuid,
      aud: selected.audience,
      exp: iat + lifetime,
      iat,
      jti: '<generated-at-runtime>',
      iss: issuerUrl
    },
    lifetimeFallbackApplied
  };
}

// Connector dropdown id; co-located so the <label htmlFor=...> and
// <select id=...> stay in sync.
const CONNECTOR_SELECT_DOM_ID = 'jwt-preview-connector-select';

export function JwtPreviewModal(props: JwtPreviewModalProps): JSX.Element {
  const siteLuid = useSelector(SessionSiteSelectors.siteLuid);

  const [searchText, setSearchText] = React.useState('');
  const [projects, setProjects] = React.useState<IProjectInfo[]>([]);
  const [loadingProjects, setLoadingProjects] = React.useState(false);
  const [selectedProject, setSelectedProject] = React.useState<IProjectInfo | null>(null);
  const [showResults, setShowResults] = React.useState(false);
  // Selection is stored as the connector luid only; the connector OBJECT is
  // re-resolved from props every render. This guarantees that when the parent
  // re-renders with a new connector object (same luid, edited audience), the
  // modal picks up the new audience rather than carrying a stale cached
  // reference.
  const [selectedConnectorLuid, setSelectedConnectorLuid] = React.useState<string>('');

  const [copiedProjectLuid, setCopiedProjectLuid] = React.useState(false);
  const [copiedSiteLuid, setCopiedSiteLuid] = React.useState(false);
  const [copiedClaims, setCopiedClaims] = React.useState(false);
  // Last clipboard error, surfaced inline so a silent rejection in non-secure
  // contexts (DOMException SecurityError, focus-not-document) is visible to
  // the user instead of disappearing into an unhandled-rejection.
  const [copyError, setCopyError] = React.useState<string | null>(null);
  // True when the parent has just dropped the previously selected connector
  // (the cleanup-effect cleared selectedConnectorLuid). The user gets a
  // transient info notice rather than a silent revert to the placeholder.
  // Cleared on the next handleConnectorChange.
  const [connectorRemovedNotice, setConnectorRemovedNotice] = React.useState(false);

  // Track copy-state-clearing timers so they can be cancelled on unmount; an
  // uncleared timeout would call setCopied(false) on an unmounted component.
  const copyResetTimeoutsRef = React.useRef<Array<ReturnType<typeof setTimeout>>>([]);
  React.useEffect(() => {
    return () => {
      copyResetTimeoutsRef.current.forEach((id) => clearTimeout(id));
      copyResetTimeoutsRef.current = [];
    };
  }, []);

  // Tracks the project name that handleProjectSelect just wrote into
  // searchText. The search effect compares against this ref to suppress the
  // round-trip for the just-picked project — including the brief interim
  // render between setSelectedProject and setSearchText when those updates
  // are not batched together (React 16 only batches inside event handlers).
  const suppressNextSearchForNameRef = React.useRef<string | null>(null);

  // Filter connectors against the WIF allowlist. The CREATE form in
  // WifConnectorsSection enforces the same list; both surfaces must agree.
  const visibleConnectors = React.useMemo(
    () => props.connectors.filter((c) => WIF_CONNECTOR_CLASSES.indexOf(c.connectorClass) !== -1),
    [props.connectors]
  );

  // Re-resolve the selected connector from the (filtered) list every render.
  // No state caching of the connector object — see selectedConnectorLuid.
  const selectedConnector = selectedConnectorLuid
    ? visibleConnectors.find((c) => c.luid === selectedConnectorLuid) || null
    : null;

  // iat refreshes whenever EITHER the project OR the connector selection
  // changes, so picking project A + connector at t=0, then changing to project
  // B at t=5min, yields a fresh iat. useMemo locks iat across unrelated
  // re-renders (typing in search, copy-state toggle, parent re-render of the
  // same connectors prop) — they do not change either key, so the memo result
  // is reused. Re-clicking the same connector value in the <select> does NOT
  // fire onChange in HTML, so the same memo key is reused and iat stays
  // stable; the user picks a different connector (or closes and reopens the
  // modal) for a fresh iat.
  const selectedProjectLuid = selectedProject ? selectedProject.luid : null;
  const iat = React.useMemo<number | null>(
    () => {
      if (!selectedProjectLuid || !selectedConnectorLuid) {
        return null;
      }
      return Math.floor(Date.now() / 1000);
    },
    [selectedProjectLuid, selectedConnectorLuid]
  );

  // If the parent removes the selected connector (or it falls outside the
  // allowlist), drop the selection so we do not render claims for a luid that
  // no longer resolves. Surface a transient info notice — silent revert to
  // placeholder leaves the modal in an unrecoverable middle state.
  React.useEffect(() => {
    if (selectedConnectorLuid && !visibleConnectors.some((c) => c.luid === selectedConnectorLuid)) {
      setSelectedConnectorLuid('');
      setConnectorRemovedNotice(true);
    }
  }, [visibleConnectors, selectedConnectorLuid]);

  React.useEffect(() => {
    if (searchText.trim().length === 0) {
      setProjects([]);
      return;
    }
    // Skip the fetch when the search text simply mirrors the just-picked
    // project's name — handleProjectSelect sets searchText to project.name,
    // which would otherwise re-fire Projects.all() for the same string with
    // showResults=false (the user sees nothing, but a round-trip happens).
    // We compare against a ref rather than selectedProject because
    // selectedProject and searchText are set in two separate setState calls;
    // outside an event handler React 16 does not batch them, so an interim
    // render briefly has the new selectedProject but still the old searchText.
    if (suppressNextSearchForNameRef.current !== null) {
      // When the suppression ref matches the current searchText, we are on
      // the render where setSearchText has landed — the selection sequence
      // is complete, clear the ref and skip the fetch. When the ref is set
      // but searchText still mirrors the previous value (interim render
      // between setSelectedProject and setSearchText, before React batches),
      // also skip the fetch — the next render will land searchText and
      // clear the ref.
      if (suppressNextSearchForNameRef.current === searchText) {
        suppressNextSearchForNameRef.current = null;
      }
      return;
    }

    let cancelled = false;
    setLoadingProjects(true);
    Projects.all()
      .withFiltersAndOrder([Filter.search(searchText)], [Order.asc(NamedItemKeys.name)])
      .getSlice(0, PROJECT_SEARCH_LIMIT)
      .then((result) => {
        if (!cancelled) {
          setProjects(result.items || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProjects([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingProjects(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [searchText, selectedProject]);

  const handleSearchChange = (value: string) => {
    // Clear the suppression ref — the user is typing, the next fetch must fire.
    suppressNextSearchForNameRef.current = null;
    setSearchText(value);
    setShowResults(true);
    // Clear selection when the user edits the search text
    if (selectedProject && value !== selectedProject.name) {
      setSelectedProject(null);
    }
    // Clear any previous clipboard error — moving back to the search input
    // means the user is back to a clean preview state.
    setCopyError(null);
  };

  const handleProjectSelect = (project: IProjectInfo) => {
    // Mark that the next render whose searchText equals project.name should
    // NOT fire a Projects fetch — the user just picked this project, the
    // round-trip would be wasted. Cleared when the effect consumes it.
    suppressNextSearchForNameRef.current = project.name;
    setSelectedProject(project);
    setSearchText(project.name);
    setShowResults(false);
    // Clear any previous clipboard error — picking a new project means the
    // user is back to a clean preview state.
    setCopyError(null);
  };

  const handleConnectorChange = (luid: string) => {
    setSelectedConnectorLuid(luid);
    // Clear any previous clipboard error — picking a new connector means the
    // user is back to a clean preview state.
    setCopyError(null);
    // The user has explicitly re-engaged with the connector dropdown — the
    // "previously selected connector is gone" notice is no longer relevant.
    setConnectorRemovedNotice(false);
  };

  const handleCopy = (text: string, setCopied: (val: boolean) => void) => {
    // clipboard-polyfill works in non-secure contexts (plain HTTP / non-localhost),
    // where navigator.clipboard is undefined. It is the clipboard helper used
    // elsewhere in the app (see PublicFacingUrlCopyButton.copyToClipboard).
    // The .catch is mandatory: writeText can reject (DOMException
    // SecurityError, permission denied, page not focused) and a missing catch
    // surfaces as an unhandled rejection with no user feedback.
    clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setCopyError(null);
        // Track the timeout so unmount cleanup can cancel it.
        const timeoutId = setTimeout(() => setCopied(false), 2000);
        copyResetTimeoutsRef.current.push(timeoutId);
      })
      .catch((err: unknown) => {
        // err can be a DOMException, a string, or anything else a clipboard
        // shim might reject with — narrow before reading .message.
        const message = err instanceof Error ? err.message : String(err);
        Tableau.Logger().warn(`JwtPreviewModal: clipboard write failed: ${message}`);
        setCopyError(Messages.authFederationJwtPreviewCopyFailed());
      });
  };

  const buildClaimsResult = buildClaims({
    project: selectedProject,
    selected: selectedConnector,
    siteLuid,
    issuerUrl: props.issuerUrl,
    tokenLifetimeSeconds: props.tokenLifetimeSeconds,
    iat
  });
  const claims = buildClaimsResult ? buildClaimsResult.claims : null;
  const lifetimeFallbackApplied = buildClaimsResult ? buildClaimsResult.lifetimeFallbackApplied : false;
  // Pin the visual key order via JSON.stringify's replacer/key list as the
  // cheapest defence against a future refactor that reorders the literal.
  const claimsJson = claims ? JSON.stringify(claims, CLAIM_KEY_ORDER as string[], 2) : '';

  const noConnectorsConfigured = visibleConnectors.length === 0;

  return (
    <DialogWidget
      title={Messages.authFederationJwtPreviewDialogTitle()}
      testId={JWT_PREVIEW_MODAL_TEST_ID}
      type={DialogType.NormalWithTitleBar}
      onCloseRequest={props.onClose}
      showCloseButton={true}
      positioningBehavior={DialogPositioningBehavior.Centered}
      // The dialog's height grows once a project + connector are picked and the
      // claims pre-block renders. Centered positioning re-centers vertically
      // after each grow, but the user's eye is anchored at the top — pulling
      // the dialog ~80px above centre keeps the bottom inside the viewport
      // when it expands without leaving the empty initial state too high.
      dialogStyle={{ marginTop: '-80px' }}
      isModal={true}
      showFooter={true}
      handleGlassClick={props.onClose}
      isGlassTransparent={false}
      footerContent={
        <DialogFooterButtonsWidget
          buttons={[
            {
              handleClick: props.onClose,
              buttonType: ButtonType.Primary,
              children: Messages.authFederationJwtPreviewDialogClose(),
              testId: JWT_PREVIEW_CLOSE_TEST_ID
            }
          ]}
        />
      }
    >
      <div className={styles.dialogContent}>
        <div className={styles.projectInputBlock}>
          <TextFieldWidget
            testId={JWT_PREVIEW_PROJECT_INPUT_TEST_ID}
            label={Messages.authFederationJwtPreviewProjectLabel()}
            value={searchText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
            placeholder={Messages.authFederationJwtPreviewProjectPlaceholder()}
          />
          {loadingProjects && (
            <div className={styles.projectInputHelperText}>
              {Messages.authFederationJwtPreviewLoadingProjects()}
            </div>
          )}
          {showResults && !loadingProjects && searchText.length > 0 && projects.length === 0 && (
            <div className={styles.projectInputHelperText}>
              {Messages.authFederationJwtPreviewNoProjectsFound()}
            </div>
          )}
          {showResults && projects.length > 0 && (
            <ul className={styles.projectResultsList}>
              {projects.map((project) => (
                <li key={project.luid}>
                  <button
                    type='button'
                    data-tb-test-id={jwtPreviewProjectResultTestId(project.luid)}
                    onClick={() => handleProjectSelect(project)}
                    className={styles.projectResultButton}
                  >
                    {project.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.connectorBlock}>
          {noConnectorsConfigured ? (
            <CompactInlineAlertWidget
              type={InlineAlertType.Warning}
              testId={JWT_PREVIEW_NO_CONNECTORS_TEST_ID}
            >
              <div>{Messages.authFederationJwtPreviewNoConnectorsConfigured()}</div>
            </CompactInlineAlertWidget>
          ) : (
            // The <label> stays inside this branch so its htmlFor never
            // points at a missing <select>: when noConnectorsConfigured is
            // true the warning replaces the dropdown, and a stranded label
            // would orphan the assistive-tech association.
            <React.Fragment>
              <label htmlFor={CONNECTOR_SELECT_DOM_ID} className={styles.fieldLabel}>
                {Messages.authFederationJwtPreviewConnectorLabel()}
              </label>
              <select
                id={CONNECTOR_SELECT_DOM_ID}
                data-tb-test-id={JWT_PREVIEW_CONNECTOR_SELECT_TEST_ID}
                value={selectedConnectorLuid}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleConnectorChange(e.target.value)}
                className={styles.connectorSelect}
              >
                <option value=''>{Messages.authFederationJwtPreviewConnectorPlaceholder()}</option>
                {visibleConnectors.map((connector) => (
                  <option key={connector.luid} value={connector.luid}>
                    {connectorClassLabel(connector.connectorClass)}
                  </option>
                ))}
              </select>
            </React.Fragment>
          )}
        </div>

        {connectorRemovedNotice && !noConnectorsConfigured && (
          // The previously-selected connector vanished from props (deleted by
          // the parent, or fell outside the WIF allowlist). Inform the admin
          // explicitly so they re-pick rather than wondering why claims
          // disappeared.
          <CompactInlineAlertWidget type={InlineAlertType.Info} testId={JWT_PREVIEW_CONNECTOR_REMOVED_TEST_ID}>
            <div>{Messages.authFederationJwtPreviewConnectorRemoved()}</div>
          </CompactInlineAlertWidget>
        )}

        {lifetimeFallbackApplied && (
          // The site config's tokenLifetimeSeconds was missing or non-numeric;
          // the claim's exp uses the 1-hour fallback. Surface the fallback so
          // the admin doesn't paste a wrong-but-plausible exp into a trust
          // policy without realising the lifetime was synthesised.
          <CompactInlineAlertWidget type={InlineAlertType.Warning} testId={JWT_PREVIEW_LIFETIME_FALLBACK_TEST_ID}>
            <div>{Messages.authFederationJwtPreviewLifetimeFallback()}</div>
          </CompactInlineAlertWidget>
        )}

        {!claims && !noConnectorsConfigured && (
          // The select-prompt is suppressed when noConnectorsConfigured is true:
          // the warning above already covers that state, and stacking two
          // alerts conveying the same actionable info is confusing.
          <CompactInlineAlertWidget type={InlineAlertType.Info} testId={JWT_PREVIEW_SELECT_PROMPT_TEST_ID}>
            <div>{Messages.authFederationJwtPreviewSelectProjectAndConnector()}</div>
          </CompactInlineAlertWidget>
        )}

        {copyError && (
          <CompactInlineAlertWidget type={InlineAlertType.Error} testId={JWT_PREVIEW_COPY_ERROR_TEST_ID}>
            <div>{copyError}</div>
          </CompactInlineAlertWidget>
        )}

        {claims && (
          <div>
            <div className={styles.luidsCard}>
              <div className={styles.luidRowSpaced}>
                <span className={styles.luidLabel}>{Messages.authFederationJwtPreviewProjectLuidLabel()}</span>
                <div>
                  <span data-tb-test-id={JWT_PREVIEW_PROJECT_LUID_TEST_ID} className={styles.luidValue}>
                    {selectedProject ? selectedProject.luid : ''}
                  </span>
                  <ButtonWidget
                    buttonType={ButtonType.Outline}
                    testId={JWT_PREVIEW_COPY_PROJECT_LUID_TEST_ID}
                    type='button'
                    onClick={() => handleCopy(selectedProject ? selectedProject.luid : '', setCopiedProjectLuid)}
                    className={styles.copyButton}
                  >
                    {copiedProjectLuid ? Messages.authFederationJwtPreviewCopied() : Messages.authFederationJwtPreviewCopy()}
                  </ButtonWidget>
                </div>
              </div>

              <div className={styles.luidRow}>
                <span className={styles.luidLabel}>{Messages.authFederationJwtPreviewSiteLuidLabel()}</span>
                <div>
                  <span data-tb-test-id={JWT_PREVIEW_SITE_LUID_TEST_ID} className={styles.luidValue}>
                    {siteLuid}
                  </span>
                  <ButtonWidget
                    buttonType={ButtonType.Outline}
                    testId={JWT_PREVIEW_COPY_SITE_LUID_TEST_ID}
                    type='button'
                    onClick={() => handleCopy(siteLuid || '', setCopiedSiteLuid)}
                    className={styles.copyButton}
                  >
                    {copiedSiteLuid ? Messages.authFederationJwtPreviewCopied() : Messages.authFederationJwtPreviewCopy()}
                  </ButtonWidget>
                </div>
              </div>
            </div>

            <div className={styles.claimsBlock}>
              <div className={styles.claimsHeader}>
                <span className={styles.luidLabel}>{Messages.authFederationJwtPreviewClaimsLabel()}</span>
                <ButtonWidget
                  buttonType={ButtonType.Outline}
                  testId={JWT_PREVIEW_COPY_CLAIMS_TEST_ID}
                  type='button'
                  onClick={() => handleCopy(claimsJson, setCopiedClaims)}
                  className={styles.copyButton}
                >
                  {copiedClaims ? Messages.authFederationJwtPreviewCopied() : Messages.authFederationJwtPreviewCopy()}
                </ButtonWidget>
              </div>
              <pre data-tb-test-id={JWT_PREVIEW_CLAIMS_TEST_ID} className={styles.claimsPre}>
                {claimsJson}
              </pre>
            </div>
          </div>
        )}
      </div>
    </DialogWidget>
  );
}
